import React, { useState } from 'react';
import { useUsersByOrganization } from '../hooks/use-fetch-hook';
import { User, Envelope } from '@phosphor-icons/react';
import { Auth0ScopeError } from './auth0-scope-error';

export const OrganizationUsers = ({ organizationName }) => {
    const { users, loading, error, refetch } = useUsersByOrganization(organizationName);
    const [selectedRole, setSelectedRole] = useState('all');
    const [currentPage, setCurrentPage] = useState(1);
    const usersPerPage = 5;

    const indexOfLastUser = currentPage * usersPerPage;
    const indexOfFirstUser = indexOfLastUser - usersPerPage;
   

    // Group users by role
    const usersByRole = users.reduce((acc, user) => {
        const role = user.user_metadata?.roles || 'unknown';
        if (!acc[role]) {
            acc[role] = [];
        }
        acc[role].push(user);
        return acc;
    }, {});

    // Get filtered users based on selected role
    const filteredUsers = selectedRole === 'all' 
        ? users 
        : usersByRole[selectedRole] || [];

    const roles = Object.keys(usersByRole);

    const currentUsers = filteredUsers.slice(indexOfFirstUser, indexOfLastUser);

    const totalPages = Math.ceil(filteredUsers.length / usersPerPage);

    if (loading) {
        return (
            <div className="p-4">
                <div className="animate-pulse">
                    <div className="h-4 bg-gray-200 rounded w-1/4 mb-4"></div>
                    <div className="space-y-3">
                        <div className="h-4 bg-gray-200 rounded"></div>
                        <div className="h-4 bg-gray-200 rounded"></div>
                        <div className="h-4 bg-gray-200 rounded"></div>
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="p-4">
                <Auth0ScopeError error={error} onRetry={refetch} />
                
                {/* Fallback error display for non-scope errors */}
                {!error.includes('insufficient_scope') && (
                    <div className="bg-red-50 border border-red-200 rounded-md p-4 mt-4">
                        <div className="flex">
                            <div className="text-red-800">
                                <p className="font-medium">Error loading users</p>
                                <p className="text-sm">{error}</p>
                            </div>
                        </div>
                        <button
                            onClick={refetch}
                            className="mt-2 bg-red-600 text-white px-3 py-1 rounded text-sm hover:bg-red-700"
                        >
                            Retry
                        </button>
                    </div>
                )}
            </div>
        );
    }

    return (
        <div>
            {/* Section Header */}
            <h4 className="p-2 text-left text-[#2DB3C0] font-['Recoleta'] underline">
                Users ({users.length})
            </h4>
            
            {/* Content */}
            <div className="p-3 bg-[#f3f9fa]">
                {/* Role Filter */}
                {roles.length > 1 && (
                    <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Filter by Role:
                        </label>
                        <select
                            value={selectedRole}
                            onChange={(e) => setSelectedRole(e.target.value)}
                            className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        >
                            <option value="all">All Roles ({users.length})</option>
                            {roles.map(role => (
                                <option key={role} value={role}>
                                    {role.charAt(0).toUpperCase() + role.slice(1)} ({usersByRole[role].length})
                                </option>
                            ))}
                        </select>
                    </div>
                )}

                {/* Users List */}

                {/* Users List */}
                {filteredUsers.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                        <User size={48} className="mx-auto mb-2 text-gray-300" />
                        <p>No users found for this organization</p>
                    </div>
                ) : (
                    <>
                        <div className="space-y-3">
                            {currentUsers.map((user, index) => (
                                <div key={user.user_id || index} className="border rounded-lg p-3 hover:bg-gray-50">
                                    <div className="flex items-start justify-between">
                                        <div className="flex-1">
                                            <div className="flex items-center space-x-2 mb-1">
                                                <h4 className="font-medium text-gray-900">
                                                    {user.user_metadata?.fullName || user.name || 'No Name'}
                                                </h4>
                                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                                    {user.user_metadata?.role || 'unknown'}
                                                </span>
                                            </div>

                                            <div className="space-y-1 text-sm text-gray-600">
                                                {user.email && (
                                                    <div className="flex items-center space-x-1">
                                                        <Envelope size={14} />
                                                        <span>{user.email}</span>
                                                    </div>
                                                )}

                                                {user.user_metadata?.skills && (
                                                    <div className="text-gray-500">
                                                        Skills: {user.user_metadata.skills}
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        <div className="text-xs text-gray-400">
                                            {user.created_at && (
                                                <div>
                                                    Joined: {new Date(user.created_at).toLocaleDateString()}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Pagination Controls */}
                        <div className="flex justify-center items-center mt-4 space-x-2">
                            <button
                                disabled={currentPage === 1}
                                onClick={() => setCurrentPage(prev => prev - 1)}
                                className="px-3 py-1 border rounded disabled:opacity-50"
                            >
                                Prev
                            </button>

                            <span className="text-sm">
                                Page {currentPage} of {totalPages}
                            </span>

                            <button
                                disabled={currentPage === totalPages}
                                onClick={() => setCurrentPage(prev => prev + 1)}
                                className="px-3 py-1 border rounded disabled:opacity-50"
                            >
                                Next
                            </button>
                        </div>
                    </>
                )}


                {/* Refresh Button */}
                <div className="mt-4 pt-4 border-t">
                    <button
                        onClick={refetch}
                        className="bg-blue-600 text-white px-4 py-2 rounded-md text-sm hover:bg-blue-700 transition-colors"
                    >
                        Refresh Users
                    </button>
                </div>
            </div>
        </div>
    );
}; 